/**
 * IPFS URI Resolution Utility
 * 
 * Converts IPFS URIs to HTTP gateway URLs for browser display.
 * Supports multiple formats:
 * - ipfs://QmHash
 * - ipfs://bafyHash
 * - ipfs/QmHash
 * - /ipfs/QmHash
 */

// Primary gateway (Pinata - fast, reliable)
const PRIMARY_GATEWAY = "https://gateway.pinata.cloud/ipfs/";

// Fallback gateway (ipfs.io - public, slower)
const FALLBACK_GATEWAY = "https://ipfs.io/ipfs/";

/**
 * Extracts CID (Content Identifier) from various IPFS URI formats
 * @param {string} uri - IPFS URI in any format
 * @returns {string|null} - Extracted CID or null if invalid
 */
export function extractCID(uri) {
  if (typeof uri !== "string" || uri.length === 0) {
    return null;
  }

  // Handle ipfs:// protocol
  if (uri.startsWith("ipfs://")) {
    return uri.replace("ipfs://", "");
  }

  // Handle /ipfs/ path
  if (uri.startsWith("/ipfs/")) {
    return uri.replace("/ipfs/", "");
  }

  // Handle ipfs/ path (without leading slash)
  if (uri.startsWith("ipfs/")) {
    return uri.replace("ipfs/", "");
  }

  // Handle Pinata gateway URLs
  if (uri.includes("gateway.pinata.cloud/ipfs/")) {
    return uri.split("gateway.pinata.cloud/ipfs/")[1].split("?")[0];
  }

  // Handle ipfs.io gateway URLs
  if (uri.includes("ipfs.io/ipfs/")) {
    return uri.split("ipfs.io/ipfs/")[1].split("?")[0];
  }

  // Handle Cloudflare gateway URLs
  if (uri.includes("cloudflare-ipfs.com/ipfs/")) {
    return uri.split("cloudflare-ipfs.com/ipfs/")[1].split("?")[0];
  }

  // If it looks like a raw CID (starts with Qm or bafy)
  if (uri.match(/^(Qm[a-zA-Z0-9]{44}|bafy[a-zA-Z0-9]+)$/)) {
    return uri;
  }

  return null;
}

/**
 * Checks if a string is an IPFS URI or path
 * @param {string} uri - String to check
 * @returns {boolean}
 */
export function isIPFSUri(uri) {
  if (typeof uri !== "string" || uri.length === 0) {
    return false;
  }

  return (
    uri.startsWith("ipfs://") ||
    uri.startsWith("/ipfs/") ||
    uri.startsWith("ipfs/") ||
    uri.includes("gateway.pinata.cloud/ipfs/") ||
    uri.includes("ipfs.io/ipfs/") ||
    uri.includes("cloudflare-ipfs.com/ipfs/") ||
    uri.match(/^(Qm[a-zA-Z0-9]{44}|bafy[a-zA-Z0-9]+)$/)
  );
}

/**
 * Converts IPFS URI to HTTP gateway URL
 * @param {string} uri - IPFS URI in any format
 * @param {string} [preferredGateway] - Optional preferred gateway URL
 * @returns {string} - HTTP gateway URL or original URI if not IPFS
 */
export function ipfsToHttp(uri, preferredGateway = PRIMARY_GATEWAY) {
  // Return original if not a string or empty
  if (typeof uri !== "string" || uri.length === 0) {
    return uri;
  }

  // Return original if already an HTTP(S) URL that's not IPFS
  if ((uri.startsWith("http://") || uri.startsWith("https://")) && !isIPFSUri(uri)) {
    return uri;
  }

  // Extract CID
  const cid = extractCID(uri);
  if (!cid) {
    return uri; // Return original if we can't extract CID
  }

  // Construct gateway URL
  return `${preferredGateway}${cid}`;
}

/**
 * Resolves image URL with IPFS support and fallback options
 * @param {string|Array} images - Image URL(s) or IPFS URI(s)
 * @param {Object} options - Resolution options
 * @param {string} [options.gateway] - Preferred IPFS gateway
 * @param {string} [options.fallback] - Fallback image URL
 * @returns {string} - Resolved HTTP URL
 */
export function resolveImageUrl(images, options = {}) {
  const { gateway = PRIMARY_GATEWAY, fallback = "" } = options;

  // Handle array of images
  if (Array.isArray(images)) {
    if (images.length === 0) {
      return fallback;
    }
    // Use first valid image
    for (const img of images) {
      if (typeof img === "string" && img.length > 0) {
        return ipfsToHttp(img, gateway);
      }
    }
    return fallback;
  }

  // Handle single image
  if (typeof images === "string" && images.length > 0) {
    return ipfsToHttp(images, gateway);
  }

  return fallback;
}

/**
 * Resolves multiple IPFS URIs to HTTP gateway URLs
 * @param {Array<string>} uris - Array of IPFS URIs
 * @param {string} [gateway] - Preferred gateway
 * @returns {Array<string>} - Array of HTTP gateway URLs
 */
export function resolveMultipleIPFS(uris, gateway = PRIMARY_GATEWAY) {
  if (!Array.isArray(uris)) {
    return [];
  }

  return uris.map(uri => ipfsToHttp(uri, gateway)).filter(Boolean);
}

/**
 * Attempts to load an IPFS image with fallback gateways
 * @param {string} ipfsUri - IPFS URI
 * @param {Array<string>} [gateways] - Array of gateway URLs to try
 * @returns {Promise<string>} - Resolves with working gateway URL
 */
export async function ipfsImageWithFallback(ipfsUri, gateways = [PRIMARY_GATEWAY, FALLBACK_GATEWAY]) {
  const cid = extractCID(ipfsUri);
  if (!cid) {
    throw new Error("Invalid IPFS URI");
  }

  for (const gateway of gateways) {
    const url = `${gateway}${cid}`;
    try {
      const response = await fetch(url, { method: "HEAD", timeout: 5000 });
      if (response.ok) {
        return url;
      }
    } catch (error) {
      console.warn(`[IPFS] Gateway ${gateway} failed for ${cid}`, error);
      continue;
    }
  }

  // If all gateways fail, return primary gateway URL anyway
  // (browser will try to load it, might work eventually)
  return `${PRIMARY_GATEWAY}${cid}`;
}

/**
 * Gets IPFS gateway URL from config or uses default
 * @returns {string} - Gateway URL
 */
export function getIPFSGateway() {
  // Check if custom gateway is configured in environment
  if (typeof process !== "undefined" && process.env && process.env.VUE_APP_IPFS_GATEWAY) {
    return process.env.VUE_APP_IPFS_GATEWAY;
  }
  return PRIMARY_GATEWAY;
}

export default {
  extractCID,
  isIPFSUri,
  ipfsToHttp,
  resolveImageUrl,
  resolveMultipleIPFS,
  ipfsImageWithFallback,
  getIPFSGateway,
  PRIMARY_GATEWAY,
  FALLBACK_GATEWAY,
};
